import { Container, Graphics, Text, TextStyle } from 'pixi.js-legacy';

/**
 * HUD component for displaying score and controls
 */
export class HUD {
  private container: Container;
  private scoreText: Text;
  private bestText: Text;
  private noMovesText: Text;
  private noMovesBackground: Graphics;
  private undoButton: Button;
  private restartButton: Button;
  private topRestartButton: Button;
  private continueButton: Button;

  constructor() {
    this.container = new Container();

    // Score display at top
    const headerY = 20;

    this.scoreText = this.createText('Score: 0', 20, headerY, 'left');
    this.bestText = this.createText('Best: 0', 1004, headerY, 'right');

    this.container.addChild(this.scoreText);
    this.container.addChild(this.bestText);

    // No moves message (centered below the "Neues Spiel" button)
    // Background for no moves text
    this.noMovesBackground = new Graphics();
    this.noMovesBackground.beginFill(0x000000, 0.5);
    this.noMovesBackground.drawRoundedRect(0, 0, 480, 50, 8);
    this.noMovesBackground.endFill();
    this.noMovesBackground.x = 512 - 240; // Center the background (512 - half width)
    this.noMovesBackground.y = 80 - 5; // 20px down from original 60, adjusted for padding
    this.noMovesBackground.visible = false;
    this.container.addChild(this.noMovesBackground);

    this.noMovesText = this.createText(
      'Es sind leider keine Züge mehr möglich',
      512,
      87, // Moved down 20px from 60
      'center'
    );
    this.noMovesText.visible = false;
    this.container.addChild(this.noMovesText);

    // Top restart button (centered)
    this.topRestartButton = new Button(
      'Neues Spiel',
      432,
      headerY - 5,
      () => {}
    );

    // Buttons at bottom (moved down to avoid overlap with 10-row grid)
    const buttonY = 610;
    this.undoButton = new Button('Undo', 100, buttonY, () => {});
    this.restartButton = new Button('Restart', 462, buttonY, () => {});

    // Continue button replaces target text at the top center
    this.continueButton = new Button(
      'Nächste Runde',
      432,
      headerY - 5,
      () => {}
    );

    this.container.addChild(this.undoButton.getContainer());
    this.container.addChild(this.restartButton.getContainer());
    this.container.addChild(this.topRestartButton.getContainer());
    this.container.addChild(this.continueButton.getContainer());

    // Initially hide continue button
    this.continueButton.setVisible(false);
  }

  /**
   * Create a text element
   */
  private createText(
    text: string,
    x: number,
    y: number,
    align: 'left' | 'center' | 'right'
  ): Text {
    const style = new TextStyle({
      fontFamily: 'Arial, sans-serif',
      fontSize: 24,
      fill: 0xffffff,
      align,
    });

    const textObj = new Text(text, style);
    textObj.x = x;
    textObj.y = y;

    if (align === 'center') {
      textObj.anchor.set(0.5, 0);
    } else if (align === 'right') {
      textObj.anchor.set(1, 0);
    }

    return textObj;
  }

  /**
   * Get the container
   */
  getContainer(): Container {
    return this.container;
  }

  /**
   * Update score display
   */
  updateScore(score: number): void {
    this.scoreText.text = `Punkte: ${score}`;
  }

  /**
   * Update best score display
   */
  updateBest(best: number): void {
    this.bestText.text = `Highscore: ${best}`;
  }

  /**
   * Set undo button enabled state
   */
  setUndoEnabled(enabled: boolean): void {
    this.undoButton.setEnabled(enabled);
  }

  /**
   * Show/hide continue button
   */
  setContinueVisible(visible: boolean): void {
    this.continueButton.setVisible(visible);
    // Hide top restart button when continue button is shown (same position)
    this.topRestartButton.setVisible(!visible);
  }

  /**
   * Show/hide no moves message
   */
  setNoMovesVisible(visible: boolean): void {
    this.noMovesText.visible = visible;
    this.noMovesBackground.visible = visible;
  }

  /**
   * Set button callbacks
   */
  setCallbacks(callbacks: {
    onUndo: () => void;
    onRestart: () => void;
    onContinue: () => void;
  }): void {
    this.undoButton.setCallback(callbacks.onUndo);
    this.restartButton.setCallback(callbacks.onRestart);
    this.topRestartButton.setCallback(callbacks.onRestart);
    this.continueButton.setCallback(callbacks.onContinue);
  }
}

/**
 * Button component
 */
class Button {
  private container: Container;
  private background: Graphics;
  private text: Text;
  private callback: () => void;
  private enabled: boolean = true;
  private visible: boolean = true;

  constructor(label: string, x: number, y: number, callback: () => void) {
    this.container = new Container();
    this.container.x = x;
    this.container.y = y;
    this.callback = callback;

    // Background
    this.background = new Graphics();
    this.drawBackground();
    this.container.addChild(this.background);

    // Text
    const style = new TextStyle({
      fontFamily: 'Arial, sans-serif',
      fontSize: 20,
      fill: 0xffffff,
    });
    this.text = new Text(label, style);
    this.text.anchor.set(0.5);
    this.text.x = 80;
    this.text.y = 20;
    this.container.addChild(this.text);

    // Interaction
    this.container.eventMode = 'static';
    this.container.cursor = 'pointer';

    this.container.on('pointerdown', () => this.handleClick());
    this.container.on('pointerover', () => this.handleHover(true));
    this.container.on('pointerout', () => this.handleHover(false));
  }

  private drawBackground(hover: boolean = false): void {
    this.background.clear();

    const color = hover && this.enabled ? 0x555555 : 0x333333;
    const alpha = this.enabled ? 1 : 0.5;

    this.background.beginFill(color, alpha);
    this.background.lineStyle(2, 0x666666, alpha);
    this.background.drawRoundedRect(0, 0, 160, 40, 8);
    this.background.endFill();
  }

  private handleClick(): void {
    if (this.enabled && this.visible) {
      this.callback();
    }
  }

  private handleHover(hover: boolean): void {
    if (this.enabled) {
      this.drawBackground(hover);
    }
  }

  getContainer(): Container {
    return this.container;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.container.cursor = enabled ? 'pointer' : 'default';
    this.drawBackground();
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.container.visible = visible;
  }

  setCallback(callback: () => void): void {
    this.callback = callback;
  }
}
